'use client'

import { FunctionComponent, useEffect } from 'react'
import classNames from 'classnames'
import Image from 'next/image'
import { AnimatePresence, motion } from 'framer-motion'
import { useSitePath } from '@/app/_providers/SellerProvider/SellerProvider'
import usePortalPassBanner from '@/hooks/usePortalPassBanner'
import { segment } from '@/utils/tracking.utils'
import styles from './PortalPassBanner.module.css'

export type Placement = 'sticky' | 'inline'

export type Props = {
  /**
   * `sticky` docks the banner above the mobile footer and minimizes past the ticket step;
   * `inline` sits in the document flow below the desktop ticket groups.
   */
  placement: Placement
}

/**
 * Slide-up + height transition for the sticky banner. Kept deliberately short so the
 * banner never fights the user's scroll.
 */
export const bannerTransition = { type: 'spring', damping: 24, stiffness: 260, mass: 0.6 } as const

/**
 * Promotional Portal Pass banner — the treatment arm of the Portal Pass interrupt
 * A/B test, replacing the blocking modal with a persistent, non-blocking promo.
 *
 * On mobile it slides up from the bottom at full height on the ticket step, then rides
 * along to add-ons as a collapsed single-line summary so it never blocks the flow. On
 * desktop it renders inline below the ticket groups, and again on add-ons, always at
 * full height. Either way a colour orbits its border to draw the eye, hovering sweeps a
 * highlight across it, and it retires itself once the user moves past add-ons.
 *
 * @param {Props} props - Component properties.
 * @returns {React.ReactElement} The banner, or an empty presence wrapper when it should not display.
 */
const PortalPassBanner: FunctionComponent<Props> = ({ placement }: Props) => {
  const { showBanner, body, bodyMinimized, minimized, url } = usePortalPassBanner()
  const sitePath = useSitePath()

  const isSticky = placement === 'sticky'

  // Desktop never minimizes — there the banner's whole job is to read as another
  // ticket choice, at full height on both the ticket and add-ons steps.
  const isMinimized = isSticky && minimized

  useEffect(() => {
    if (showBanner) segment.portalPassBannerViewed({ seller: sitePath, placement, minimized: isMinimized })
  }, [showBanner, sitePath, placement, isMinimized])

  const text = isMinimized ? bodyMinimized : body

  /*
    The visibility check lives *inside* AnimatePresence rather than short-circuiting
    above it: AnimatePresence can only play an exit transition for a keyed child that
    leaves while AnimatePresence itself stays mounted. Returning null early would tear
    the two out together and the banner would pop off screen instead of retiring.
  */
  return (
    <div className={classNames({ [styles.StickyViewport]: isSticky })}>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            key="portal-pass-banner"
            className={classNames(styles.Banner, {
              [styles.Sticky]: isSticky,
              [styles.Inline]: !isSticky,
              [styles.Minimized]: isMinimized,
            })}
            initial={isSticky ? { y: '100%' } : { opacity: 0, scale: 0.98 }}
            animate={isSticky ? { y: 0 } : { opacity: 1, scale: 1 }}
            exit={isSticky ? { y: '100%' } : { opacity: 0, scale: 0.98 }}
            transition={bannerTransition}
            data-testid="portal-pass-banner"
            data-minimized={isMinimized}
          >
            <a
              className={styles.Link}
              href={url}
              onClick={() => segment.portalPassBannerClicked({ seller: sitePath, placement, minimized: isMinimized })}
              data-testid="portal-pass-banner-link"
            >
              <span className={styles.Text}>{text}</span>
              <Image
                className={styles.Chevron}
                src="/static/svg/chevron-pink.svg"
                alt=""
                aria-hidden="true"
                width={10}
                height={16}
              />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default PortalPassBanner
